import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import Consts from '../utils/constants';
import Formulas from '../utils/formulas';
import Utils from '../utils/utils';
import cloneDeep from 'lodash.clonedeep';

export default class PlotDataServiceService extends Service {
    @service('data') model;
    @service plotData;
    @service logging;
    @service selectionData;

    @computed(
        'selectionData.{selectedDriver,selectedAdc}',
        'model.{samplingRate,vref,rf,rext,cext,signalRms,filterBandwidth,vout,thd,wbNoise,noiseConfig,frequency,adjustedSourceThd,thdSettlingAdjustment,adcEquivNoiseBandwidth}',
    )
    get noiseArrays() {
        return this.calculateNoiseArrays();
    }

    _getCurveByType(frequency, type) {
        let threeDbFrequency = this.model.filterBandwidth,
            corner = this.get(`selectionData.selectedDriver.${type}cornerTEMP`),
            slope = this.get(`selectionData.selectedDriver.${type}slopeTEMP`),
            flat = -1 * this.get(`selectionData.selectedDriver.${type}flatTEMP`),
            degredation = Formulas.calculateDegredation(frequency, corner, slope),
            curveUnfiltered = Formulas.calculateHdCurveUnfiltered(degredation, flat);

        return Formulas[`calculate${type}Curve`](frequency, curveUnfiltered, threeDbFrequency);
    }

    _calculateAmplifierCurves(frequency) {
        let hd2 = this._getCurveByType(frequency, 'HD2'),
            hd3 = this._getCurveByType(frequency, 'HD3'),
            noiseDefaults = this.plotData.noiseDefaults;

        return {
            hd2:
                this.selectionData.selectedAdc.type === Consts.ADC_TYPE.SE ||
                this.selectionData.selectedDriver.type === Consts.DRIVER_TYPE.DIFF_AMP
                    ? hd2
                    : noiseDefaults.curve.hd2,
            hd3: hd3,
            secondInvAmp:
                this.model.noiseConfig === Consts.CONFIGURATION.DiffADC_SEAmp_SESource
                    ? hd3
                    : noiseDefaults.curve.secondInvAmp,
            mirroredAmpCurve: noiseDefaults.curve.mirroredAmpCurve,
        };
    }

    _calculateFrequency(previousFrequency) {
        let endFrequency = (this.model.samplingRate * 0.5) / this.model.averaging,
            startFrequency = Math.pow(10, (Math.log10(endFrequency) - +4).toFixed(0));
        if (previousFrequency)
            return Math.pow(10, Math.log10(previousFrequency) + Math.log10(endFrequency / startFrequency) / 80);
        else return Math.pow(10, Math.round(Math.log10(startFrequency)));
    }

    _calculateThdAmpOnly(frequency, hdCurveData) {
        if (
            Utils.isNumeric(this.model.selectedAdc.filteredAliasTHD) &&
            frequency > this.model.adcEquivNoiseBandwidth / 2
        )
            return this.model.selectedAdc.filteredAliasTHD;

        let combinedLinearTHDAmp =
            Math.pow(10, hdCurveData.hd2 / 10) +
            Math.pow(10, hdCurveData.hd3 / 10) +
            Math.pow(10, hdCurveData.secondInvAmp / 10) +
            Math.pow(10, hdCurveData.mirroredAmpCurve / 10);

        // WAS adjust amp THD for Vout value
        // Now, convert Volts^2 to dB
        return 20 * Math.log10(Math.sqrt(combinedLinearTHDAmp));
    }

    _calculateAdcThd(frequency) {
        if (
            Utils.isNumeric(this.model.selectedAdc.filteredAliasTHD) &&
            frequency > this.model.adcEquivNoiseBandwidth / 2
        )
            return this.model.selectedAdc.filteredAliasTHD;

        let thdSettlingAdjustment = this.model.thdSettlingAdjustment;
        let rext = this.model.rext;
        let adjustedTHD = 0;

        if (thdSettlingAdjustment === false) {
            adjustedTHD = Formulas.calculateAdjustedThd(frequency, rext, this.selectionData.selectedAdc, -500);
        } else {
            adjustedTHD = Formulas.calculateAdjustedThd(
                frequency,
                rext,
                this.selectionData.selectedAdc,
                thdSettlingAdjustment,
            );
        }
        return adjustedTHD;
    }

    _calculateSystemThd(adjustedSourceThd, thdAmpOnly, adcThd) {
        return (
            20 *
            Math.log10(Math.pow(10, adjustedSourceThd / 20) + Math.pow(10, thdAmpOnly / 20) + Math.pow(10, adcThd / 20))
        );
    }

    _calculatePlotData(frequency, hdCurveData) {
        let adjustedSourceThd = this.model.adjustedSourceThd,
            systemSnr = this.model.noiseCalculations.total.totalSnr,
            adcSnr = this.model.noiseCalculations.adc.totalSnr,
            voutRms = this.model.signalRms,
            threeDbFrequency = this.model.filterBandwidth,
            sourceOnlyNoise = this.model.noiseCalculations.source.totalVrms,
            ampOnlyNoise = this.model.noiseCalculations.driver.totalVrms,
            voutAmp = voutRms / (1 + frequency / threeDbFrequency),
            snrSourceOnly = 20 * Math.log10(voutAmp / sourceOnlyNoise),
            snrAmpOnly = 20 * Math.log10(voutAmp / ampOnlyNoise),
            thdAmpOnly = this._calculateThdAmpOnly(frequency, hdCurveData),
            adcThd = this._calculateAdcThd(frequency),
            systemThd = this._calculateSystemThd(adjustedSourceThd, thdAmpOnly, adcThd),
            thd = {
                source: adjustedSourceThd,
                amp: thdAmpOnly,
                adc: adcThd,
                system: systemThd,
            },
            sinad = {
                source: Formulas.calculateSinad(snrSourceOnly, adjustedSourceThd),
                amp: Formulas.calculateSinad(snrAmpOnly, thdAmpOnly),
                adc: Formulas.calculateSinad(adcSnr, adcThd),
                system: Formulas.calculateSinad(systemSnr, systemThd),
            },
            enob = {
                source: Formulas.calculateEnob(sinad.source),
                amp: Formulas.calculateEnob(sinad.amp),
                adc: Formulas.calculateEnob(sinad.adc),
                system: Formulas.calculateEnob(sinad.system),
            };

        return {
            thd: thd,
            sinad: sinad,
            enob: enob,
            frequency: frequency,
            hdCurveData: hdCurveData,
        };
    }

    _findIndexAtF(frequencyArray) {
        let min,
            chosen = 0,
            fLen = frequencyArray.length;

        for (let f = 0; f < fLen; f++) {
            min = Math.abs(frequencyArray[chosen] - this.model.frequency);

            if (Math.abs(frequencyArray[f] - this.model.frequency) < min) chosen = f;
        }

        return chosen;
    }

    _mapPlotData(plotData, type) {
        let config = cloneDeep(this.plotData.getNoiseAndDistortionConfig(type)),
            plotItem = plotData.map((d) => d[type]);

        config.source = plotItem.map((d) => d.source);
        config.amp = plotItem.map((d) => d.amp);
        config.adc = plotItem.map((d) => d.adc);
        config.system = plotItem.map((d) => d.system);
        return config;
    }

    calculateNoiseArrays() {
        let plotData = [];

        for (let i = 0; i < 81; i++) {
            // build frequency array
            let frequency = this._calculateFrequency(i > 0 ? plotData[i - 1].frequency : undefined),
                // Building HD2 and HD3 curves for Amplifier:
                hdCurveData = this._calculateAmplifierCurves(frequency);

            plotData[i] = this._calculatePlotData(frequency, hdCurveData);
        }

        this.logging.logNoiseData(
            plotData,
            this.selectionData.selectedAdc,
            this.selectionData.selectedDriver,
            this.model.adjustedSourceThd,
        );

        // Find the index at F
        let frequencyArray = plotData.map((d) => d.frequency),
            indexAtF = this._findIndexAtF(frequencyArray),
            plotDataAtF = plotData[indexAtF];

        return {
            frequencyArray: frequencyArray,
            noisePlotData: {
                thd: this._mapPlotData(plotData, 'thd'),
                sinad: this._mapPlotData(plotData, 'sinad'),
                enob: this._mapPlotData(plotData, 'enob'),
            },

            indexAtFreq: indexAtF,
            sinadAtF: plotDataAtF.sinad.system,
            enobAtF: plotDataAtF.enob.system,
            thdAmpAtF: plotDataAtF.thd.amp,
            thdAdcAtF: plotDataAtF.thd.adc,
            systemThdAtF: plotDataAtF.thd.system,
        };
    }
}
